import { APP_VERSION } from '../version';
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * Compara duas versões em formato semver (ex: "1.11.9" vs "1.11.8")
 * Retorna: 1 se v1 > v2, -1 se v1 < v2, 0 se iguais
 */
export function compareVersions(v1, v2) {
    if (!v1 || !v2) return 0;
    const parts1 = v1.replace(/[^0-9.]/g, '').split('.').map(n => parseInt(n, 10) || 0);
    const parts2 = v2.replace(/[^0-9.]/g, '').split('.').map(n => parseInt(n, 10) || 0);
    const maxLen = Math.max(parts1.length, parts2.length);

    for (let i = 0; i < maxLen; i++) {
        const p1 = parts1[i] || 0;
        const p2 = parts2[i] || 0;
        if (p1 > p2) return 1;
        if (p1 < p2) return -1;
    }
    return 0;
}

// Plugin nativo de atualização — só existe no APK de distribuição direta
let _appUpdater = null;
function getAppUpdaterPlugin() {
    if (_appUpdater) return _appUpdater;
    if (!Capacitor.isNativePlatform()) return null;
    try {
        _appUpdater = registerPlugin('AppUpdater');
        return _appUpdater;
    } catch {
        return null;
    }
}

/**
 * Lê a versão realmente instalada no dispositivo.
 * No APK usa o versionCode nativo (mais confiável que o versionName do package.json).
 */
async function getInstalledVersion() {
    const plugin = getAppUpdaterPlugin();
    if (plugin) {
        try {
            const info = await plugin.getAppVersion();
            return {
                versionName: String(info?.versionName || APP_VERSION).replace(/-direct$/i, ''),
                versionCode: info?.versionCode != null ? Number(info.versionCode) : null,
            };
        } catch (e) {
            console.warn('directUpdateService: Falha ao ler versão nativa, usando fallback:', e);
        }
    }
    return { versionName: APP_VERSION, versionCode: null };
}

/**
 * Verifica se há uma atualização disponível para o APK direto
 * Consulta primeiro o arquivo version-direct.json no hosting público e, se falhar, tenta o Firestore.
 * @returns {Promise<{ hasUpdate: boolean, latestVersion?: string, apkUrl?: string, releaseNotes?: string, mandatory?: boolean }>}
 */
export async function checkDirectUpdate() {
    let updateData = null;

    // 1. Tenta obter do hosting público (rápido, sem custo de leitura no Firestore)
    try {
        const cacheBuster = `?_cb=${Date.now()}`;
        const response = await fetch(`https://meupainel-21dba.web.app/version-direct.json${cacheBuster}`, {
            cache: 'no-store'
        });
        if (response.ok) {
            updateData = await response.json();
        }
    } catch (e) {
        console.warn('directUpdateService: Falha ao consultar version-direct.json via HTTP, tentando Firestore...', e);
    }

    // 2. Fallback para Firestore (app_config -> direct_release)
    if (!updateData) {
        try {
            const configDocRef = doc(db, 'app_config', 'direct_release');
            const docSnap = await getDoc(configDocRef);
            if (docSnap.exists()) {
                updateData = docSnap.data();
            }
        } catch (e) {
            console.warn('directUpdateService: Falha ao consultar Firestore app_config/direct_release:', e);
        }
    }

    if (!updateData || !updateData.latestVersion) {
        return { hasUpdate: false };
    }

    const installed = await getInstalledVersion();
    const currentVersion = installed.versionName;
    const remoteCode = updateData.versionCode != null ? Number(updateData.versionCode) : null;

    // Prioriza o versionCode nativo; usa semver apenas como fallback (web/plugin indisponível)
    const isNewer = (remoteCode != null && installed.versionCode != null)
        ? remoteCode > installed.versionCode
        : compareVersions(updateData.latestVersion, currentVersion) > 0;

    console.log(
        `[directUpdateService] instalado=${currentVersion} (${installed.versionCode}) | ` +
        `remoto=${updateData.latestVersion} (${remoteCode}) | hasUpdate=${isNewer}`
    );

    return {
        hasUpdate: isNewer,
        currentVersion,
        latestVersion: updateData.latestVersion,
        versionCode: updateData.versionCode,
        apkUrl: updateData.apkUrl,
        releaseNotes: updateData.releaseNotes || 'Novas melhorias e correções disponíveis.',
        mandatory: !!updateData.mandatory
    };
}
